(function () {
  'use strict';

  if (window.PetomgJourneyBridge && window.PetomgJourneyBridge.version) return;

  var VERSION = 2;
  var publishQueue = [];
  var flushTimer = null;
  var dedupCache = new Map();
  var formContexts = new WeakMap();
  var lastContextByVariant = new Map();
  var lastQuickAddContext = null;
  var lastCartSummary = null;
  var lastAddResultAt = 0;
  var cartDrawerEpisode = null;
  var cartDrawerEpisodeSequence = 0;
  var backgroundVideoStates = new WeakMap();
  var backgroundVideoStateList = [];
  var BACKGROUND_VIDEO_MILESTONES = [25, 50, 75, 95];
  var BACKGROUND_VIDEO_CHECKPOINT_SECONDS = [3, 5, 10];
  var BACKGROUND_VIDEO_IMPRESSION_DELAY = 1000;
  var PUBLISH_KEYS = new Set([
    'source', 'source_component', 'result', 'action', 'error_kind', 'error_code',
    'old_quantity', 'new_quantity', 'cart_item_count', 'section_id', 'template',
    'product_handle', 'collection_handle', 'parent_product_id',
    'visible_product_count', 'opener_source', 'card_context',
    'has_default_variant', 'discount_code_count', 'has_subscription',
    'button_type', 'target_anchor', 'product_id', 'variant_id', 'sku',
    'quantity', 'cart_total_minor', 'currency', 'country_code',
    'drawer_episode_id', 'open_duration_ms', 'close_reason',
    'cart_line_count', 'cart_product_count', 'cart_total_quantity',
    'interaction_area', 'recommendation_type', 'checkout_was_visible',
    'recommendation_click_count', 'recommendation_add_count',
    'cart_update_count', 'media_id', 'media_variant', 'media_provider',
    'autoplay', 'muted', 'loop', 'lazyload', 'duration_ms', 'milestone',
    'watch_time_ms', 'visible_time_ms', 'max_progress_percent',
    'stall_count', 'stall_duration_ms', 'play_count', 'startup_ms',
    'checkpoint_seconds', 'is_custom_checkpoint', 'end_reason', 'end_stage',
    'cta_block_id', 'destination_type', 'stage', 'stage_index',
    'previous_stage', 'previous_stage_index', 'source_stage', 'file_selected',
    'upload_required', 'file_count_bucket', 'rejected_file_count_bucket',
    'accept_group', 'missing_required_field_count', 'pfb_required',
    'pfb_ready', 'pfb_preflight_passed', 'pfb_mapped_field_count',
    'pfb_missing_field_count', 'pfb_value_mismatch_count',
    'form_submit_succeeded', 'cta_visible', 'cta_enabled',
    'variant_available', 'product_available', 'price_minor',
    'max_stage_index', 'step_1_validation_failed_count',
    'step_2_validation_failed_count', 'pfb_submit_attempted',
    'pfb_submit_succeeded', 'payment_revealed', 'payment_cta_clicked',
    'product_revealed', 'add_result_observed', 'final_blocker_code',
    'button_state'
  ]);

  function canPublish() {
    return window.Shopify
      && window.Shopify.analytics
      && typeof window.Shopify.analytics.publish === 'function';
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function flushQueue() {
    if (!canPublish()) return;

    while (publishQueue.length) {
      var queued = publishQueue.shift();
      try {
        window.Shopify.analytics.publish(queued.name, queued.payload);
      } catch (error) {
        // Keep analytics bridge failures isolated from storefront behavior.
      }
    }

    if (flushTimer) {
      window.clearInterval(flushTimer);
      flushTimer = null;
    }
  }

  function scheduleFlush() {
    if (flushTimer) return;

    flushTimer = window.setInterval(flushQueue, 500);
    window.setTimeout(function () {
      if (flushTimer) {
        window.clearInterval(flushTimer);
        flushTimer = null;
      }
    }, 10000);
  }

  function cleanPayload(payload) {
    var clean = {};

    Object.keys(payload).forEach(function (key) {
      var value = payload[key];

      if (value === undefined || value === null || value === '') return;

      if (typeof value === 'string') {
        clean[key] = value.slice(0, 160);
        return;
      }

      if ((typeof value === 'number' && Number.isFinite(value)) || typeof value === 'boolean') {
        clean[key] = value;
      }
    });

    return clean;
  }

  function getPageType() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    return meta && meta.page && meta.page.pageType ? meta.page.pageType : undefined;
  }

  function getCurrency(cart) {
    if (cart && cart.currency) return cart.currency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return undefined;
  }

  function getMarket() {
    return window.Shopify && window.Shopify.country ? window.Shopify.country : undefined;
  }

  function commonPayload(extra) {
    return cleanPayload(Object.assign({
      template: getPageType(),
      country_code: getMarket(),
      currency: getCurrency()
    }, extra || {}));
  }

  function publishedPayload(payload) {
    var filtered = {};

    Object.keys(payload).forEach(function (key) {
      if (PUBLISH_KEYS.has(key)) filtered[key] = payload[key];
    });

    return filtered;
  }

  function dedupKey(parts) {
    return parts.map(function (part) {
      if (part === undefined || part === null || part === '') return 'na';
      return String(part).replace(/\s+/g, '_').slice(0, 80);
    }).join('|');
  }

  function shouldSkip(key, ttl) {
    var now = Date.now();
    var previous = dedupCache.get(key);

    if (previous && now - previous < ttl) return true;

    dedupCache.set(key, now);

    if (dedupCache.size > 200) {
      dedupCache.forEach(function (timestamp, cacheKey) {
        if (now - timestamp > 30000) dedupCache.delete(cacheKey);
      });
    }

    return false;
  }

  function publish(name, payload, parts, ttl) {
    if (isDesignMode()) return;

    var key = dedupKey([name].concat(parts || []));

    if (shouldSkip(key, ttl || 2000)) return;

    var finalPayload = publishedPayload(commonPayload(payload));

    if (canPublish()) {
      try {
        window.Shopify.analytics.publish(name, finalPayload);
      } catch (error) {
        // Keep analytics bridge failures isolated from storefront behavior.
      }
      return;
    }

    publishQueue.push({ name: name, payload: finalPayload });
    scheduleFlush();
  }

  function handleExternalPublish(evt) {
    var detail = evt.detail || {};
    var name = typeof detail.name === 'string' ? detail.name : '';

    if (name.indexOf('petomg:fds_') !== 0) return;

    publish(
      name,
      detail.payload || {},
      Array.isArray(detail.dedupParts) ? detail.dedupParts : [],
      Number(detail.ttl) || 2000
    );
  }

  function pathFromUrl(url) {
    if (!url) return undefined;

    try {
      return new URL(url, window.location.origin).pathname;
    } catch (error) {
      return String(url).split('?')[0];
    }
  }

  function handleFromProductPath(path) {
    if (!path || path.indexOf('/products/') === -1) return undefined;
    return path.split('/products/')[1].split('/')[0];
  }

  function handleFromCollectionPath(path) {
    if (!path || path.indexOf('/collections/') === -1) return undefined;
    return path.split('/collections/')[1].split('/')[0];
  }

  function sectionIdFromElement(el) {
    var section = el && el.closest ? el.closest('.shopify-section') : null;
    if (!section || !section.id) return undefined;
    return section.id.replace('shopify-section-', '');
  }

  function stringId(value) {
    if (value === undefined || value === null || value === '') return undefined;
    return String(value).slice(0, 128);
  }

  function canonicalSource(value) {
    var source = String(value || 'unknown')
      .trim()
      .toLowerCase()
      .replace(/\|/g, '/')
      .replace(/[^a-z0-9_.:/-]+/g, '_')
      .slice(0, 64);
    return source || 'unknown';
  }

  function getFormValue(form, name) {
    var input = form && form.querySelector ? form.querySelector('[name="' + name + '"]') : null;
    return input ? input.value : undefined;
  }

  function skuFromForm(form, variantId) {
    var rawSkus = form && form.dataset ? form.dataset.skus : '';
    if (!rawSkus || !variantId) return undefined;

    var pairs = rawSkus.split(';');
    for (var i = 0; i < pairs.length; i += 1) {
      var parts = pairs[i].split(':');
      if (parts[0] === String(variantId)) return parts[1];
    }

    return undefined;
  }

  function sourceComponent(source, host) {
    if (source && source.indexOf('linked-products-slider') !== -1) return 'linked_products';
    if (source === 'product_page') return 'product_form';
    if (source && source.indexOf('product-card') === 0) return 'product_card';
    if (host && host.closest && host.closest('quick-add-drawer')) return 'quick_add';
    return source || 'unknown';
  }

  function quickAddContextFromButton(button) {
    if (!button) return null;

    var path = pathFromUrl(button.dataset.productUrl);
    var inLinkedProducts = Boolean(button.closest('.replacement-parts-section'));
    var inProductCard = Boolean(button.closest('product-card'));
    var recommendation = recommendationContext(button);

    return cleanPayload({
      source: recommendation
        ? recommendation.type
        : (inLinkedProducts
        ? 'product-card/linked-products-slider'
        : (inProductCard ? 'product-card' : 'quick-add')),
      source_component: recommendation ? 'cart_drawer_recommendation' : 'quick_add',
      section_id: sectionIdFromElement(button),
      product_handle: handleFromProductPath(path),
      product_url_path: path,
      selected_color: button.dataset.selectedColor
    });
  }

  function contextFromForm(form) {
    if (!form) return {};

    var host = form.closest('product-form');
    var quickAddDrawer = host && host.closest ? host.closest('quick-add-drawer') : null;
    var variantId = getFormValue(form, 'id');
    var quickContext = quickAddDrawer ? lastQuickAddContext : null;
    var source = canonicalSource(getFormValue(form, '_add_source'));

    if (quickContext && quickContext.source) {
      source = quickContext.source;
    }

    return cleanPayload({
      source: source,
      source_component: quickContext && quickContext.source_component
        ? quickContext.source_component
        : sourceComponent(source, host),
      section_id: quickContext && quickContext.section_id
        ? quickContext.section_id
        : sectionIdFromElement(form),
      variant_id: stringId(variantId),
      sku: skuFromForm(form, variantId),
      quantity: Number(getFormValue(form, 'quantity') || 1),
      product_handle: quickContext && quickContext.product_handle,
      product_url_path: quickContext && quickContext.product_url_path
    });
  }

  function rememberFormContext(form) {
    var host = form && form.closest ? form.closest('product-form') : null;
    var context = contextFromForm(form);

    if (host) formContexts.set(host, context);
    if (context.variant_id) lastContextByVariant.set(String(context.variant_id), context);

    window.setTimeout(function () {
      if (context.variant_id) lastContextByVariant.delete(String(context.variant_id));
    }, 30000);
  }

  function contextForProductFormEvent(evt) {
    var host = evt.target && evt.target.closest ? evt.target.closest('product-form') : null;
    var variantId = evt.detail && evt.detail.variantId;
    var context = host ? formContexts.get(host) : null;

    if (!context && variantId) context = lastContextByVariant.get(String(variantId));
    if (!context && host) context = contextFromForm(host.querySelector('.js-product-form'));

    return context || {};
  }

  function cartSummary(cart) {
    if (!cart) return {};

    var productIds = new Set();
    var items = Array.isArray(cart.items) ? cart.items : [];
    items.forEach(function (item) {
      if (item && item.product_id !== undefined && item.product_id !== null) {
        productIds.add(String(item.product_id));
      }
    });

    var summary = cleanPayload({
      cart_item_count: Number(cart.item_count),
      cart_line_count: items.length,
      cart_product_count: productIds.size,
      cart_total_quantity: Number(cart.item_count),
      cart_total_minor: Number(cart.total_price),
      currency: getCurrency(cart)
    });

    lastCartSummary = summary;
    return summary;
  }

  function drawerEpisodeId() {
    cartDrawerEpisodeSequence += 1;
    return 'drawer-' + Date.now().toString(36) + '-' + cartDrawerEpisodeSequence.toString(36);
  }

  function activeCartDrawer() {
    return document.querySelector('cart-drawer[open]:not([aria-hidden="true"])');
  }

  function cartDrawerSummaryFromDom(drawer) {
    if (!drawer) return {};

    var lineItems = Array.from(drawer.querySelectorAll('.cart-item[data-product-id]'));
    var productIds = new Set();
    var totalQuantity = 0;

    lineItems.forEach(function (item) {
      if (item.dataset.productId) productIds.add(item.dataset.productId);
      var input = item.querySelector('input[name="updates[]"], input[name="quantity"]');
      var quantity = input ? Number(input.value) : 0;
      if (Number.isFinite(quantity)) totalQuantity += quantity;
    });

    return cleanPayload({
      cart_item_count: totalQuantity,
      cart_line_count: lineItems.length,
      cart_product_count: productIds.size,
      cart_total_quantity: totalQuantity
    });
  }

  async function refreshCartSummary(drawer) {
    var domSummary = cartDrawerSummaryFromDom(drawer);
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

    try {
      var response = await window.fetch(root + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (response.ok) return cartSummary(await response.json());
    } catch (error) {
      // The drawer remains usable; DOM counts are a safe analytics fallback.
    }

    return Object.assign({}, lastCartSummary || {}, domSummary);
  }

  function episodePayload(extra) {
    if (!cartDrawerEpisode) return Object.assign({}, lastCartSummary || {}, extra || {});
    return Object.assign({}, lastCartSummary || {}, {
      drawer_episode_id: cartDrawerEpisode.id
    }, extra || {});
  }

  function setCartDrawerCloseReason(reason) {
    if (!cartDrawerEpisode || cartDrawerEpisode.closePublished) return;
    cartDrawerEpisode.closeReason = reason;
  }

  function observeCheckoutVisibility(drawer, episode) {
    var checkout = drawer && drawer.querySelector('[name="checkout"]');
    if (!checkout) return;

    if (!('IntersectionObserver' in window)) {
      var rect = checkout.getBoundingClientRect();
      episode.checkoutWasVisible = rect.top < window.innerHeight && rect.bottom > 0;
      return;
    }

    episode.checkoutObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          episode.checkoutWasVisible = true;
        }
      });
    }, { threshold: [0.5] });
    episode.checkoutObserver.observe(checkout);
  }

  function recommendationContext(target) {
    var recommendations = target && target.closest ? target.closest('product-recommendations.cart-recommendations') : null;
    var promoted = target && target.closest ? target.closest('promoted-products') : null;
    if (!recommendations && !promoted) return null;
    return {
      area: recommendations ? 'recommendations' : 'promoted_products',
      type: recommendations ? 'recommendations' : 'promoted_products'
    };
  }

  function publishCartDrawerInteraction(action, target, extra) {
    if (!cartDrawerEpisode) return;

    var recommendation = recommendationContext(target);
    if (recommendation && (action === 'product_clicked' || action === 'quick_add_opened')) {
      cartDrawerEpisode.recommendationClickCount += 1;
    }

    publish('petomg:cart_drawer_interacted', episodePayload(Object.assign({
      source: 'cart_drawer',
      source_component: recommendation ? 'cart_drawer_recommendation' : 'cart_drawer',
      action: action,
      interaction_area: recommendation ? recommendation.area : 'cart_items',
      recommendation_type: recommendation ? recommendation.type : undefined
    }, extra || {})), [
      cartDrawerEpisode.id,
      action,
      recommendation && recommendation.area,
      extra && (extra.product_id || extra.variant_id)
    ], 750);
  }

  function findCartItem(cart, variantId) {
    if (!cart || !cart.items || !variantId) return null;

    for (var i = 0; i < cart.items.length; i += 1) {
      if (Number(cart.items[i].variant_id) === Number(variantId)) {
        return cart.items[i];
      }
    }

    return null;
  }

  function classifyError(message) {
    var text = String(message || '').toLowerCase();

    if (!text) return 'unknown';
    if (/^[0-9]+$/.test(text)) return 'http_status';
    if (text.indexOf('sold out') !== -1) return 'sold_out';
    if (text.indexOf('unavailable') !== -1 || text.indexOf('not available') !== -1) return 'unavailable';
    if (text.indexOf('quantity') !== -1 || text.indexOf('stock') !== -1) return 'quantity_or_stock';
    return 'cart_error';
  }

  function sourceFromCartContainer(el) {
    if (el && el.closest && el.closest('cart-drawer')) return 'cart_drawer';
    if (el && el.closest && el.closest('.cc-main-cart')) return 'cart_page';
    return 'cart';
  }

  function boolFromData(value) {
    return value === true || value === 'true';
  }

  function numberMs(seconds) {
    var value = Number(seconds);
    if (!Number.isFinite(value) || value < 0) return undefined;
    return Math.round(value * 1000);
  }

  function numberSeconds(value) {
    var seconds = Number(value);
    if (!Number.isFinite(seconds) || seconds <= 0) return undefined;
    return Math.round(seconds);
  }

  function activeBackgroundVideoMedia(media) {
    if (!media) return false;

    if (window.getComputedStyle) {
      var style = window.getComputedStyle(media);
      if (!style || style.display === 'none' || style.visibility === 'hidden') return false;
    }

    if (media.getBoundingClientRect) {
      var rect = media.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;
    }

    return true;
  }

  function backgroundVideoBasePayload(state, extra) {
    return cleanPayload(Object.assign({
      source: 'background_video',
      source_component: 'background_video',
      section_id: state.sectionId,
      media_id: state.mediaId,
      media_variant: state.mediaVariant,
      media_provider: state.mediaProvider,
      autoplay: state.autoplay,
      muted: state.muted,
      loop: state.loop,
      lazyload: state.lazyload
    }, extra || {}));
  }

  function backgroundVideoStageRank(stage) {
    if (!stage) return 0;
    if (stage === 'dom_ready') return 10;
    if (stage === 'poster_ready') return 20;
    if (stage === 'impression') return 30;
    if (stage === 'started') return 40;
    if (stage === 'completed') return 10000;

    var match = String(stage).match(/^played_([0-9]+)s$/);
    return match ? 100 + Number(match[1]) : 0;
  }

  function markBackgroundVideoStage(state, stage) {
    if (!stage) return;
    if (backgroundVideoStageRank(stage) >= backgroundVideoStageRank(state.endStage)) {
      state.endStage = stage;
    }
  }

  function backgroundVideoCheckpointStage(seconds) {
    return 'played_' + String(seconds) + 's';
  }

  function backgroundVideoCheckpointSeconds(state) {
    var seconds = BACKGROUND_VIDEO_CHECKPOINT_SECONDS.slice();

    if (state.customCheckpointSeconds && seconds.indexOf(state.customCheckpointSeconds) === -1) {
      seconds.push(state.customCheckpointSeconds);
    }

    return seconds.sort(function (a, b) {
      return a - b;
    });
  }

  function afterBackgroundVideoPaint(callback) {
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(callback);
      });
      return;
    }

    window.setTimeout(callback, 0);
  }

  function publishBackgroundVideoDomReady(state) {
    if (state.domReadyPublished || !activeBackgroundVideoMedia(state.media)) return;

    state.domReadyPublished = true;
    markBackgroundVideoStage(state, 'dom_ready');
    publish('petomg:background_video_dom_ready', backgroundVideoBasePayload(state), [
      state.sectionId,
      state.mediaId,
      state.mediaVariant,
      window.location && window.location.pathname
    ], 60000);
  }

  function publishBackgroundVideoPosterReady(state) {
    if (state.posterReadyPublished || !activeBackgroundVideoMedia(state.media)) return;
    if (!state.domReadyPublished) return;
    if (state.posterReadyQueued) return;

    state.posterReadyQueued = true;
    afterBackgroundVideoPaint(function () {
      state.posterReadyQueued = false;
      if (state.posterReadyPublished || !state.domReadyPublished || !activeBackgroundVideoMedia(state.media)) return;

      state.posterReadyPublished = true;
      markBackgroundVideoStage(state, 'poster_ready');
      publish('petomg:background_video_poster_ready', backgroundVideoBasePayload(state), [
        state.sectionId,
        state.mediaId,
        state.mediaVariant,
        window.location && window.location.pathname
      ], 60000);
    });
  }

  function refreshBackgroundVideoCounters(state) {
    var now = Date.now();

    if (state.visible && state.visibleStartedAt) {
      state.visibleTimeMs += now - state.visibleStartedAt;
      state.visibleStartedAt = now;
    }

    if (state.playing && state.visible && state.watchStartedAt) {
      state.watchTimeMs += now - state.watchStartedAt;
      state.watchStartedAt = now;
    }

    if (state.stallStartedAt) {
      state.stallDurationMs += now - state.stallStartedAt;
      state.stallStartedAt = now;
    }
  }

  function setBackgroundVideoVisible(state, visible) {
    if (visible && !activeBackgroundVideoMedia(state.media)) visible = false;
    if (state.visible === visible) return;

    refreshBackgroundVideoCounters(state);
    state.visible = visible;

    if (visible) {
      state.visibleStartedAt = Date.now();
      if (!state.firstVisibleAt) state.firstVisibleAt = state.visibleStartedAt;
      if (state.playing) state.watchStartedAt = state.visibleStartedAt;
      publishBackgroundVideoDomReady(state);
      observeBackgroundVideoPoster(state);
      if (state.playing) publishBackgroundVideoStarted(state);
      scheduleBackgroundVideoImpression(state);
      return;
    }

    state.visibleStartedAt = null;
    state.watchStartedAt = null;
    if (state.impressionTimer) {
      window.clearTimeout(state.impressionTimer);
      state.impressionTimer = null;
    }
  }

  function setBackgroundVideoPlaying(state, playing) {
    if (state.playing === playing) return;

    refreshBackgroundVideoCounters(state);
    state.playing = playing;

    if (playing) {
      state.playCount += 1;
      state.watchStartedAt = state.visible ? Date.now() : null;
      return;
    }

    state.watchStartedAt = null;
  }

  function scheduleBackgroundVideoImpression(state) {
    if (state.impressionPublished || state.impressionTimer) return;

    state.impressionTimer = window.setTimeout(function () {
      state.impressionTimer = null;
      if (state.impressionPublished || !state.visible || !activeBackgroundVideoMedia(state.media)) return;

      state.impressionPublished = true;
      markBackgroundVideoStage(state, 'impression');
      publish('petomg:background_video_impression', backgroundVideoBasePayload(state), [
        state.sectionId,
        state.mediaId,
        state.mediaVariant,
        window.location && window.location.pathname
      ], 60000);
    }, BACKGROUND_VIDEO_IMPRESSION_DELAY);
  }

  function publishBackgroundVideoStarted(state) {
    if (state.started || !state.visible || !activeBackgroundVideoMedia(state.media)) return;

    state.started = true;
    state.startedAt = Date.now();
    markBackgroundVideoStage(state, 'started');
    if (state.firstVisibleAt && state.startupMs === undefined) {
      state.startupMs = Math.max(0, state.startedAt - state.firstVisibleAt);
    }

    publish('petomg:background_video_started', backgroundVideoBasePayload(state, {
      startup_ms: state.startupMs
    }), [
      state.sectionId,
      state.mediaId,
      state.mediaVariant,
      window.location && window.location.pathname
    ], 60000);
  }

  function backgroundVideoDurationMs(state, video) {
    if (state.durationMs !== undefined) return state.durationMs;
    if (!video) return undefined;

    var durationMs = numberMs(video.duration);
    if (durationMs !== undefined && durationMs > 0) state.durationMs = durationMs;
    return state.durationMs;
  }

  function publishBackgroundVideoProgress(state, video) {
    if (!video || state.mediaProvider !== 'shopify' || !state.visible) return;

    var duration = Number(video.duration);
    var currentTime = Number(video.currentTime);
    if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(currentTime)) return;

    var progress = Math.min(100, Math.max(0, Math.floor((currentTime / duration) * 100)));
    state.maxProgressPercent = Math.max(state.maxProgressPercent, progress);
    backgroundVideoDurationMs(state, video);
    publishBackgroundVideoSecondCheckpoints(state, video);

    BACKGROUND_VIDEO_MILESTONES.forEach(function (milestone) {
      if (progress < milestone || state.progressMilestones[milestone]) return;

      state.progressMilestones[milestone] = true;
      publish('petomg:background_video_progress', backgroundVideoBasePayload(state, {
        milestone: milestone,
        duration_ms: state.durationMs,
        max_progress_percent: state.maxProgressPercent
      }), [
        state.sectionId,
        state.mediaId,
        state.mediaVariant,
        milestone,
        window.location && window.location.pathname
      ], 60000);
    });

    if (progress >= 95) publishBackgroundVideoCompleted(state, video);
  }

  function publishBackgroundVideoSecondCheckpoints(state, video) {
    var currentTime = Number(video.currentTime);
    if (!Number.isFinite(currentTime) || currentTime < 0) return;

    backgroundVideoCheckpointSeconds(state).forEach(function (seconds) {
      if (currentTime < seconds || state.secondCheckpoints[seconds]) return;

      var stage = backgroundVideoCheckpointStage(seconds);
      state.secondCheckpoints[seconds] = true;
      markBackgroundVideoStage(state, stage);
      refreshBackgroundVideoCounters(state);
      publish('petomg:background_video_checkpoint', backgroundVideoBasePayload(state, {
        checkpoint_seconds: seconds,
        is_custom_checkpoint: BACKGROUND_VIDEO_CHECKPOINT_SECONDS.indexOf(seconds) === -1,
        duration_ms: state.durationMs,
        watch_time_ms: Math.round(state.watchTimeMs),
        max_progress_percent: state.maxProgressPercent
      }), [
        state.sectionId,
        state.mediaId,
        state.mediaVariant,
        seconds,
        window.location && window.location.pathname
      ], 60000);
    });
  }

  function publishBackgroundVideoCompleted(state, video) {
    if (state.completedPublished || !state.visible || !activeBackgroundVideoMedia(state.media)) return;

    backgroundVideoDurationMs(state, video);
    refreshBackgroundVideoCounters(state);
    state.completedPublished = true;
    state.maxProgressPercent = Math.max(state.maxProgressPercent, 95);
    markBackgroundVideoStage(state, 'completed');
    publish('petomg:background_video_completed', backgroundVideoBasePayload(state, {
      duration_ms: state.durationMs,
      watch_time_ms: Math.round(state.watchTimeMs),
      max_progress_percent: state.maxProgressPercent
    }), [
      state.sectionId,
      state.mediaId,
      state.mediaVariant,
      window.location && window.location.pathname
    ], 60000);
  }

  function startBackgroundVideoStall(state) {
    if (state.stallStartedAt) return;
    refreshBackgroundVideoCounters(state);
    state.stallCount += 1;
    state.stallStartedAt = Date.now();
  }

  function finishBackgroundVideoStall(state) {
    if (!state.stallStartedAt) return;
    refreshBackgroundVideoCounters(state);
    state.stallStartedAt = null;
  }

  function publishBackgroundVideoFailure(state, errorKind) {
    if (state.failurePublished || !state.visible || !activeBackgroundVideoMedia(state.media)) return;

    state.failurePublished = true;
    publish('petomg:background_video_playback_failed', backgroundVideoBasePayload(state, {
      result: 'failure',
      error_kind: errorKind || 'media_error'
    }), [
      state.sectionId,
      state.mediaId,
      state.mediaVariant,
      errorKind || 'media_error',
      window.location && window.location.pathname
    ], 60000);
  }

  function publishBackgroundVideoSummary(state, endReason) {
    if (!state || state.summaryPublished) return;

    refreshBackgroundVideoCounters(state);
    finishBackgroundVideoStall(state);

    if (!state.impressionPublished && !state.started && state.visibleTimeMs <= 0) return;

    var summaryPayload = {
      visible_time_ms: Math.round(state.visibleTimeMs),
      end_reason: endReason || 'unknown',
      end_stage: state.endStage || 'unknown'
    };

    if (state.mediaProvider === 'shopify') {
      Object.assign(summaryPayload, {
        duration_ms: state.durationMs,
        watch_time_ms: Math.round(state.watchTimeMs),
        max_progress_percent: state.maxProgressPercent,
        stall_count: state.stallCount,
        stall_duration_ms: Math.round(state.stallDurationMs),
        play_count: state.playCount,
        startup_ms: state.startupMs
      });
    }

    state.summaryPublished = true;
    publish('petomg:background_video_summary', backgroundVideoBasePayload(state, summaryPayload), [
      state.sectionId,
      state.mediaId,
      state.mediaVariant,
      endReason || 'unknown',
      window.location && window.location.pathname
    ], 60000);
  }

  function backgroundVideoStateFromElement(el) {
    if (!el || !el.closest) return null;
    var media = el.closest('[data-petomg-background-video-media="true"]');
    return media ? backgroundVideoStates.get(media) : null;
  }

  function copyBackgroundVideoComponentData(state, component) {
    if (!component || !component.dataset) return;
    state.sectionId = component.dataset.petomgSectionId || state.sectionId;
    state.mediaId = component.dataset.petomgMediaId || state.mediaId;
    state.mediaVariant = component.dataset.petomgMediaVariant || state.mediaVariant;
    state.mediaProvider = component.dataset.petomgMediaProvider || state.mediaProvider;
    state.autoplay = boolFromData(component.dataset.petomgAutoplay);
    state.muted = boolFromData(component.dataset.petomgMuted);
    state.loop = boolFromData(component.dataset.petomgLoop);
    state.lazyload = boolFromData(component.dataset.petomgLazyload);
    state.customCheckpointSeconds = numberSeconds(component.dataset.petomgCustomCheckpointSeconds)
      || state.customCheckpointSeconds;
  }

  function attachNativeBackgroundVideo(state, video) {
    if (!video || state.video === video) return;
    state.video = video;

    video.addEventListener('loadedmetadata', function () {
      backgroundVideoDurationMs(state, video);
      publishBackgroundVideoProgress(state, video);
    });

    video.addEventListener('play', function () {
      if (!state.firstPlayAttemptAt) state.firstPlayAttemptAt = Date.now();
    });

    video.addEventListener('playing', function () {
      finishBackgroundVideoStall(state);
      setBackgroundVideoPlaying(state, true);
      backgroundVideoDurationMs(state, video);
      publishBackgroundVideoStarted(state);
      publishBackgroundVideoProgress(state, video);
    });

    video.addEventListener('timeupdate', function () {
      refreshBackgroundVideoCounters(state);
      publishBackgroundVideoProgress(state, video);
    });

    video.addEventListener('waiting', function () {
      startBackgroundVideoStall(state);
    });

    video.addEventListener('stalled', function () {
      startBackgroundVideoStall(state);
    });

    video.addEventListener('canplay', function () {
      finishBackgroundVideoStall(state);
    });

    video.addEventListener('pause', function () {
      finishBackgroundVideoStall(state);
      setBackgroundVideoPlaying(state, false);
    });

    video.addEventListener('ended', function () {
      finishBackgroundVideoStall(state);
      publishBackgroundVideoProgress(state, video);
      publishBackgroundVideoCompleted(state, video);
      setBackgroundVideoPlaying(state, false);
    });

    video.addEventListener('error', function () {
      finishBackgroundVideoStall(state);
      setBackgroundVideoPlaying(state, false);
      publishBackgroundVideoFailure(state, 'media_error');
    });
  }

  function checkExternalBackgroundVideoStarted(state) {
    if (!state.component || state.started || !activeBackgroundVideoMedia(state.media)) return;

    var player = state.component.player;

    if (state.mediaProvider === 'youtube' && player && typeof player.getPlayerState === 'function') {
      try {
        if (player.getPlayerState() === 1) {
          publishBackgroundVideoStarted(state);
        }
      } catch (error) {
        // External player polling is best-effort and must not affect playback.
      }
      return;
    }

    if (state.mediaProvider === 'vimeo' && player && typeof player.getPaused === 'function') {
      try {
        player.getPaused().then(function (paused) {
          if (!paused) {
            publishBackgroundVideoStarted(state);
          }
        }).catch(function () {});
      } catch (error) {
        // External player polling is best-effort and must not affect playback.
      }
    }
  }

  function attachBackgroundVideoComponent(state) {
    var component = state.media.querySelector('[data-petomg-background-video="true"]');
    if (!component) return;

    state.component = component;
    copyBackgroundVideoComponentData(state, component);
    attachNativeBackgroundVideo(state, component.querySelector('video'));

    if (state.mediaProvider !== 'shopify' && !state.externalStartedTimer) {
      state.externalStartedTimer = window.setInterval(function () {
        checkExternalBackgroundVideoStarted(state);
        if (state.started) {
          window.clearInterval(state.externalStartedTimer);
          state.externalStartedTimer = null;
        }
      }, 1000);

      window.setTimeout(function () {
        if (state.externalStartedTimer) {
          window.clearInterval(state.externalStartedTimer);
          state.externalStartedTimer = null;
        }
      }, 30000);
    }
  }

  function observeBackgroundVideoPoster(state) {
    var image = state.media.querySelector('.media img');
    if (!image) return;

    if (image.complete && image.naturalWidth !== 0) {
      publishBackgroundVideoPosterReady(state);
      return;
    }

    if (state.posterObserved) return;
    state.posterObserved = true;
    image.addEventListener('load', function () {
      publishBackgroundVideoPosterReady(state);
    }, { once: true });
  }

  function observeBackgroundVideoMedia(state) {
    if ('IntersectionObserver' in window === false) {
      setBackgroundVideoVisible(state, activeBackgroundVideoMedia(state.media));
      return;
    }

    state.observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        setBackgroundVideoVisible(state, entry.isIntersecting && entry.intersectionRatio >= 0.5);
      });
    }, { threshold: [0, 0.5, 1] });
    state.observer.observe(state.media);
  }

  function initBackgroundVideoMedia(media) {
    if (!media || backgroundVideoStates.has(media)) return;

    var state = {
      media: media,
      section: media.closest('[data-petomg-background-video-section="true"]'),
      sectionId: media.dataset.petomgSectionId || sectionIdFromElement(media),
      mediaId: media.dataset.petomgMediaId,
      mediaVariant: media.dataset.petomgMediaVariant,
      mediaProvider: media.dataset.petomgMediaProvider,
      autoplay: boolFromData(media.dataset.petomgAutoplay),
      muted: boolFromData(media.dataset.petomgMuted),
      loop: boolFromData(media.dataset.petomgLoop),
      lazyload: boolFromData(media.dataset.petomgLazyload),
      customCheckpointSeconds: numberSeconds(media.dataset.petomgCustomCheckpointSeconds),
      visible: false,
      playing: false,
      domReadyPublished: false,
      posterReadyPublished: false,
      impressionPublished: false,
      started: false,
      completedPublished: false,
      failurePublished: false,
      summaryPublished: false,
      posterReadyQueued: false,
      progressMilestones: {},
      secondCheckpoints: {},
      endStage: null,
      maxProgressPercent: 0,
      visibleTimeMs: 0,
      watchTimeMs: 0,
      stallCount: 0,
      stallDurationMs: 0,
      playCount: 0
    };

    backgroundVideoStates.set(media, state);
    backgroundVideoStateList.push(state);
    attachBackgroundVideoComponent(state);
    observeBackgroundVideoPoster(state);

    if (window.MutationObserver) {
      state.mutationObserver = new window.MutationObserver(function () {
        attachBackgroundVideoComponent(state);
        observeBackgroundVideoPoster(state);
      });
      state.mutationObserver.observe(media, { childList: true, subtree: true });
    }

    observeBackgroundVideoMedia(state);
  }

  function initBackgroundVideoTracking(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (!scope || typeof scope.querySelectorAll !== 'function') return;

    var mediaItems = Array.from(scope.querySelectorAll('[data-petomg-background-video-media="true"]'));

    if (root && root.matches && root.matches('[data-petomg-background-video-media="true"]')) {
      mediaItems.unshift(root);
    }

    mediaItems.forEach(initBackgroundVideoMedia);
  }

  function activeBackgroundVideoState(section) {
    var states = backgroundVideoStateList.filter(function (state) {
      return state.section === section && activeBackgroundVideoMedia(state.media);
    });

    return states[0] || null;
  }

  function backgroundVideoDestinationType(link) {
    if (!link || link.getAttribute('aria-disabled') === 'true' || !link.getAttribute('href')) {
      return 'disabled';
    }

    try {
      var url = new URL(link.getAttribute('href'), window.location.origin);
      return url.origin === window.location.origin ? 'internal' : 'external';
    } catch (error) {
      return link.dataset.petomgDestinationType || 'internal';
    }
  }

  function handleBackgroundVideoCtaClick(evt) {
    var link = evt.target && evt.target.closest
      ? evt.target.closest('[data-petomg-background-video-cta="true"]')
      : null;

    if (!link) return;

    var section = link.closest('[data-petomg-background-video-section="true"]');
    var state = activeBackgroundVideoState(section);
    var destinationType = backgroundVideoDestinationType(link);

    publish('petomg:background_video_cta_clicked', backgroundVideoBasePayload(state || {
      sectionId: section && section.dataset.petomgSectionId,
      mediaId: undefined,
      mediaVariant: undefined,
      mediaProvider: undefined
    }, {
      cta_block_id: link.dataset.petomgCtaBlockId,
      destination_type: destinationType
    }), [
      section && section.dataset.petomgSectionId,
      state && state.mediaId,
      state && state.mediaVariant,
      link.dataset.petomgCtaBlockId,
      destinationType
    ], 3000);

    if (state) publishBackgroundVideoSummary(state, 'cta_navigation');
  }

  function handleBackgroundVideoPageHide() {
    backgroundVideoStateList.forEach(function (state) {
      publishBackgroundVideoSummary(state, 'pagehide');
    });
  }

  function handleBackgroundVideoVisibilityChange() {
    if (document.visibilityState !== 'hidden') return;
    backgroundVideoStateList.forEach(function (state) {
      publishBackgroundVideoSummary(state, 'visibility_hidden');
    });
  }

  function handleBackgroundVideoSectionUnload(evt) {
    var section = evt.target && evt.target.matches && evt.target.matches('[data-petomg-background-video-section="true"]')
      ? evt.target
      : evt.target && evt.target.querySelector && evt.target.querySelector('[data-petomg-background-video-section="true"]');

    if (!section) return;

    backgroundVideoStateList.forEach(function (state) {
      if (state.section === section) publishBackgroundVideoSummary(state, 'section_unload');
    });
  }

  function handleProductFormSubmit(evt) {
    var form = evt.target;
    if (!form || !form.matches || !form.matches('.js-product-form')) return;

    rememberFormContext(form);
  }

  function handleCartAdd(evt) {
    var detail = evt.detail || {};
    var cart = detail.cart;
    var item = findCartItem(cart, detail.variantId);
    var context = contextForProductFormEvent(evt);
    var payload = Object.assign({}, context, cartSummary(cart), {
      result: 'added',
      variant_id: stringId(detail.variantId),
      product_id: item && stringId(item.product_id),
      sku: item && item.sku ? item.sku : context.sku,
      quantity: item && Number(item.quantity)
    });

    lastAddResultAt = Date.now();

    if (cartDrawerEpisode && recommendationContext(evt.target)) {
      cartDrawerEpisode.recommendationAddCount += 1;
    } else if (cartDrawerEpisode && context.source_component === 'cart_drawer_recommendation') {
      cartDrawerEpisode.recommendationAddCount += 1;
    }

    if (cartDrawerEpisode) payload.drawer_episode_id = cartDrawerEpisode.id;

    publish('petomg:add_to_cart_result', payload, [
      payload.source,
      payload.variant_id,
      payload.quantity,
      payload.cart_item_count
    ], 2500);
  }

  function handleLineItemChange(evt) {
    var target = evt.target;
    var detail = evt.detail || {};

    if (target && target.matches && target.matches('product-form')) {
      handleProductFormQuantityChange(evt);
      return;
    }

    if (target && target.matches && target.matches('cart-items')) {
      handleCartLineUpdate(evt);
    }
  }

  function handleProductFormQuantityChange(evt) {
    var detail = evt.detail || {};
    var cart = detail.cart;
    var item = findCartItem(cart, detail.variantId);
    var context = contextForProductFormEvent(evt);
    var payload = Object.assign({}, context, cartSummary(cart), {
      result: 'quantity_changed',
      variant_id: stringId(detail.variantId),
      product_id: item && stringId(item.product_id),
      sku: item && item.sku ? item.sku : context.sku,
      old_quantity: Number(detail.oldQuantity),
      new_quantity: Number(detail.newQuantity),
      quantity: Number(detail.newQuantity)
    });

    lastAddResultAt = Date.now();

    publish('petomg:add_to_cart_result', payload, [
      payload.source,
      payload.variant_id,
      payload.old_quantity,
      payload.new_quantity,
      payload.cart_item_count
    ], 2500);
  }

  function handleCartLineUpdate(evt) {
    var detail = evt.detail || {};
    var cart = detail.cart;
    var oldQuantity = Number(detail.oldQuantity);
    var newQuantity = Number(detail.newQuantity);
    var action = 'update';

    if (newQuantity === 0) {
      action = 'remove';
    } else if (newQuantity > oldQuantity) {
      action = 'increase';
    } else if (newQuantity < oldQuantity) {
      action = 'decrease';
    }

    var payload = Object.assign({}, cartSummary(cart), {
      source: sourceFromCartContainer(evt.target),
      source_component: 'cart_items',
      result: 'success',
      action: action,
      variant_id: stringId(detail.variantId),
      old_quantity: oldQuantity,
      new_quantity: newQuantity
    });

    if (cartDrawerEpisode && payload.source === 'cart_drawer') {
      cartDrawerEpisode.cartUpdateCount += 1;
      payload.drawer_episode_id = cartDrawerEpisode.id;
    }

    publish('petomg:cart_line_update_result', payload, [
      payload.source,
      payload.action,
      payload.variant_id,
      payload.old_quantity,
      payload.new_quantity,
      payload.cart_item_count
    ], 2500);
  }

  function handleCartError(evt) {
    var target = evt.target;
    var detail = evt.detail || {};
    var errorKind = classifyError(detail.error);

    if (target && target.matches && target.matches('cart-items')) {
      publish('petomg:cart_line_update_result', {
        source: sourceFromCartContainer(target),
        source_component: 'cart_items',
        result: 'failure',
        error_kind: errorKind
      }, [
        sourceFromCartContainer(target),
        errorKind
      ], 5000);
      return;
    }

    if (target && target.matches && target.matches('product-form')) {
      var context = contextForProductFormEvent(evt);
      publish('petomg:add_to_cart_failed', Object.assign({}, context, {
        result: 'failure',
        error_kind: errorKind
      }), [
        context.source,
        context.variant_id,
        errorKind
      ], 5000);
    }
  }

  function handleQuickAddClick(evt) {
    var button = evt.target && evt.target.closest ? evt.target.closest('.js-quick-add') : null;
    if (!button) return;

    lastQuickAddContext = quickAddContextFromButton(button);
  }

  function handleQuickAddOpened(evt) {
    var drawer = evt.target;
    if (!drawer || !drawer.matches || !drawer.matches('quick-add-drawer')) return;

    var context = quickAddContextFromButton(drawer.opener) || lastQuickAddContext || {};

    publish('petomg:quick_add_opened', context, [
      context.source_component,
      context.product_handle,
      context.section_id
    ], 2500);

    monitorQuickAddLoad(drawer, context);
  }

  function monitorQuickAddLoad(drawer, context) {
    var token = [
      context.product_url_path || 'unknown',
      Date.now()
    ].join('|');

    drawer.dataset.petomgJourneyQuickAddToken = token;

    window.setTimeout(function () {
      if (drawer.dataset.petomgJourneyQuickAddToken !== token) return;
      if (drawer.getAttribute('aria-hidden') === 'true') return;

      var loaded = !drawer.classList.contains('is-loading')
        && Boolean(drawer.querySelector('.quick-add__content .product-title, .quick-add__content .product-price'));

      if (loaded) return;

      publish('petomg:quick_add_load_failed', Object.assign({}, context, {
        result: 'failure',
        error_kind: 'content_not_loaded'
      }), [
        context.product_handle,
        context.section_id,
        'content_not_loaded'
      ], 15000);
    }, 15000);
  }

  function cartDrawerOpenerSource(drawer) {
    var opener = drawer && drawer.opener;

    if (Date.now() - lastAddResultAt < 5000) return 'after_add';
    if (opener && opener.id === 'cart-icon') return 'cart_icon';
    if (opener && opener.matches && opener.matches('.js-open-cart-drawer')) return 'quick_add_view_cart';
    if (opener) return 'explicit_opener';

    return 'programmatic';
  }

  function handleCartDrawerOpened(evt) {
    var drawer = evt.target;
    if (!drawer || !drawer.matches || !drawer.matches('cart-drawer')) return;

    var openerSource = cartDrawerOpenerSource(drawer);

    if (cartDrawerEpisode && !cartDrawerEpisode.closePublished) {
      publishCartDrawerClosed('reopened');
    }

    cartDrawerEpisode = {
      id: drawerEpisodeId(),
      openedAt: Date.now(),
      closeReason: null,
      closePublished: false,
      checkoutWasVisible: false,
      recommendationClickCount: 0,
      recommendationAddCount: 0,
      cartUpdateCount: 0,
      checkoutObserver: null
    };

    var episode = cartDrawerEpisode;
    lastCartSummary = Object.assign(
      {},
      lastCartSummary || {},
      cartDrawerSummaryFromDom(drawer)
    );
    observeCheckoutVisibility(drawer, episode);

    publish('petomg:cart_drawer_opened', episodePayload({
      source: 'cart_drawer',
      source_component: 'cart_drawer',
      opener_source: openerSource
    }), [
      episode.id,
      openerSource,
      lastCartSummary && lastCartSummary.cart_item_count,
      lastCartSummary && lastCartSummary.cart_total_minor
    ], 2500);

    refreshCartSummary(drawer).then(function (summary) {
      if (cartDrawerEpisode !== episode) return;
      lastCartSummary = Object.assign({}, lastCartSummary || {}, summary);
    });
  }

  function publishCartDrawerClosed(fallbackReason) {
    if (!cartDrawerEpisode || cartDrawerEpisode.closePublished) return;

    var episode = cartDrawerEpisode;
    episode.closePublished = true;
    if (episode.checkoutObserver) episode.checkoutObserver.disconnect();

    publish('petomg:cart_drawer_closed', episodePayload({
      source: 'cart_drawer',
      source_component: 'cart_drawer',
      open_duration_ms: Math.max(0, Date.now() - episode.openedAt),
      close_reason: episode.closeReason || fallbackReason || 'unknown',
      checkout_was_visible: episode.checkoutWasVisible,
      recommendation_click_count: episode.recommendationClickCount,
      recommendation_add_count: episode.recommendationAddCount,
      cart_update_count: episode.cartUpdateCount
    }), [episode.id], 0);
  }

  function handleCartDrawerClosed(evt) {
    if (!evt.target || !evt.target.matches || !evt.target.matches('cart-drawer')) return;
    publishCartDrawerClosed('programmatic');
  }

  function handleCartDrawerClick(evt) {
    var drawer = activeCartDrawer();
    if (!drawer) return;

    var target = evt.target && evt.target.closest ? evt.target.closest('*') : null;
    if (!target) return;

    if (target.closest('.js-close-drawer')) {
      setCartDrawerCloseReason('close_button');
      return;
    }

    if (target.matches('.js-overlay') || target.closest('.overlay--drawer')) {
      setCartDrawerCloseReason('overlay');
      return;
    }

    var checkout = target.closest('[name="checkout"], a[href*="/checkout"]');
    if (checkout && checkout.closest('cart-drawer')) {
      setCartDrawerCloseReason('checkout');
      return;
    }

    var viewCart = target.closest('.cart-drawer__view-cart, a[href="/cart"]');
    if (viewCart && viewCart.closest('cart-drawer')) {
      setCartDrawerCloseReason('view_cart');
      publishCartDrawerInteraction('view_cart_clicked', viewCart);
      return;
    }

    var quickAdd = target.closest('.js-quick-add');
    if (quickAdd && quickAdd.closest('cart-drawer')) {
      var quickPath = pathFromUrl(quickAdd.dataset.productUrl);
      publishCartDrawerInteraction('quick_add_opened', quickAdd, {
        product_handle: handleFromProductPath(quickPath)
      });
      return;
    }

    var productLink = target.closest('a[href*="/products/"]');
    if (productLink && productLink.closest('cart-drawer')) {
      var productPath = pathFromUrl(productLink.getAttribute('href'));
      setCartDrawerCloseReason('product_navigation');
      publishCartDrawerInteraction('product_clicked', productLink, {
        product_handle: handleFromProductPath(productPath),
        product_id: stringId(productLink.closest('[data-product-id]') && productLink.closest('[data-product-id]').dataset.productId)
      });
    }
  }

  function handleCartDrawerKeyup(evt) {
    if (evt.key === 'Escape' && activeCartDrawer()) setCartDrawerCloseReason('escape');
  }

  function handlePageHide() {
    if (!cartDrawerEpisode || cartDrawerEpisode.closePublished) return;
    publishCartDrawerClosed(cartDrawerEpisode.closeReason || 'page_unload');
  }

  function handleCheckoutClick(evt) {
    var target = evt.target && evt.target.closest
      ? evt.target.closest('button[name="checkout"], [type="submit"][name="checkout"], a[href*="/checkout"]')
      : null;

    if (!target || target.disabled || target.getAttribute('aria-disabled') === 'true') return;

    var source = 'unknown';
    if (target.closest('cart-drawer')) source = 'cart_drawer';
    else if (target.closest('.cc-main-cart') || target.closest('form[action*="/cart"]')) source = 'cart_page';
    else if (target.closest('product-form')) source = 'product_form';

    publish('petomg:checkout_cta_clicked', episodePayload({
      source: source,
      source_component: 'checkout_cta',
      button_type: 'checkout'
    }), [
      source,
      lastCartSummary && lastCartSummary.cart_item_count,
      lastCartSummary && lastCartSummary.cart_total_minor
    ], 3000);

    if (source === 'cart_drawer') setCartDrawerCloseReason('checkout');
  }

  function handleLinkedCollectionClick(evt) {
    var card = evt.target && evt.target.closest
      ? evt.target.closest('.replacement-parts-section__collection-card')
      : null;

    if (!card) return;

    var section = card.closest('.replacement-parts-section');
    var path = pathFromUrl(card.getAttribute('href'));
    var productMeta = window.ShopifyAnalytics
      && window.ShopifyAnalytics.meta
      && window.ShopifyAnalytics.meta.product;

    publish('petomg:linked_collection_clicked', {
      source: 'linked-products-slider',
      source_component: 'linked_products',
      section_id: sectionIdFromElement(card),
      parent_product_id: productMeta && stringId(productMeta.id),
      collection_handle: handleFromCollectionPath(path),
      visible_product_count: section
        ? section.querySelectorAll('.replacement-parts-section__item:not(.replacement-parts-section__item--collection)').length
        : undefined
    }, [
      productMeta && productMeta.id,
      handleFromCollectionPath(path)
    ], 3000);
  }

  function handleStickyAtcClick(evt) {
    var link = evt.target && evt.target.closest
      ? evt.target.closest('sticky-atc-panel a[href^="#"]')
      : null;

    if (!link) return;

    var productMeta = window.ShopifyAnalytics
      && window.ShopifyAnalytics.meta
      && window.ShopifyAnalytics.meta.product;

    publish('petomg:sticky_atc_clicked', {
      source: 'sticky_atc_panel',
      source_component: 'sticky_atc',
      parent_product_id: productMeta && stringId(productMeta.id),
      target_anchor: link.getAttribute('href').replace('#', '')
    }, [
      productMeta && productMeta.id,
      link.getAttribute('href')
    ], 3000);
  }

  document.addEventListener('submit', handleProductFormSubmit, true);
  document.addEventListener('click', handleQuickAddClick, true);
  document.addEventListener('click', handleCartDrawerClick, true);
  document.addEventListener('keyup', handleCartDrawerKeyup, true);
  document.addEventListener('click', handleCheckoutClick, true);
  document.addEventListener('click', handleLinkedCollectionClick, true);
  document.addEventListener('click', handleStickyAtcClick, true);
  document.addEventListener('click', handleBackgroundVideoCtaClick, true);
  document.addEventListener('petomg:journey:publish', handleExternalPublish, true);
  document.addEventListener('on:cart:add', handleCartAdd, true);
  document.addEventListener('on:line-item:change', handleLineItemChange, true);
  document.addEventListener('on:cart:error', handleCartError, true);
  document.addEventListener('on:quickbuy:after-open', handleQuickAddOpened, true);
  document.addEventListener('on:cart-drawer:after-open', handleCartDrawerOpened, true);
  document.addEventListener('on:cart-drawer:after-close', handleCartDrawerClosed, true);
  document.addEventListener('shopify:section:load', function (evt) {
    initBackgroundVideoTracking(evt.target);
  });
  document.addEventListener('shopify:section:unload', handleBackgroundVideoSectionUnload);
  document.addEventListener('visibilitychange', handleBackgroundVideoVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('pagehide', handleBackgroundVideoPageHide);
  initBackgroundVideoTracking(document);

  window.PetomgJourneyBridge = {
    version: VERSION,
    disabled: isDesignMode(),
    publish: publish,
    flush: flushQueue
  };

  flushQueue();
}());
